import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, filesAPI, requisitosEtapaAPI, etapasAPI, marcasEtapaAPI, atividadesAPI, usersAPI } from '../lib/api';
import { filtrarUsuariosAtribuicao } from '../lib/users';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { ScrollArea } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import ClientQuickViewModal from './ClientQuickViewModal';
import ParcelasEditor from './ParcelasEditor';
import LaudosEditor from './LaudosEditor';
import {
  CheckCircle,
  Circle,
  Clock,
  AlertTriangle,
  Plus,
  MessageSquare,
  MessageCircle,
  FileText,
  Download,
  Upload,
  Trash2,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Archive,
  XCircle,
  Eye,
  DollarSign,
  Pencil,
  Tag,
  Lock,
  History,
  ClipboardList,
  Calendar as CalendarIcon,
  X,
  PauseCircle,
  PlayCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';
import axios from 'axios';
import { useLimiteUpload } from '../lib/uploadLimit';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrencyInput = (value) => {
  let digits = value.replace(/\D/g, '');
  if (!digits) return '';
  let number = parseInt(digits, 10);
  let formatted = (number / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return 'R$ ' + formatted;
};

const parseCurrencyInput = (formatted) => {
  if (!formatted) return 0;
  let clean = formatted.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.');
  return parseFloat(clean) || 0;
};

const ProjectTimeline = ({ project, etapas: etapasFromProps, onUpdate }) => {
  const limiteUpload = useLimiteUpload();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showClientQuickView, setShowClientQuickView] = useState(false);
  const [pendenciaDialog, setPendenciaDialog] = useState(false);
  const [observacaoDialog, setObservacaoDialog] = useState(false);
  const [documentosDialog, setDocumentosDialog] = useState(false);
  const [projetoDialog, setProjetoDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [newPendencia, setNewPendencia] = useState('');
  const [newObservacao, setNewObservacao] = useState('');
  const [cancelMotivo, setCancelMotivo] = useState('');
  const [files, setFiles] = useState([]);
  const [projetoFiles, setProjetoFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingProjeto, setUploadingProjeto] = useState(false);
  const [numeroContrato, setNumeroContrato] = useState(project.numero_contrato || '');
  const [valorServico, setValorServico] = useState(
    project.valor_servico ? formatCurrencyInput(Math.round(project.valor_servico * 100).toString()) : ''
  );
  const [requisitos, setRequisitos] = useState([]);
  const [loadingRequisitos, setLoadingRequisitos] = useState(true);
  const [marcas, setMarcas] = useState([]);
  const [marcasValores, setMarcasValores] = useState(project.marcas_valores || {});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [etapas, setEtapas] = useState([]);
  // Atividades do projeto
  const [atividades, setAtividades] = useState([]);
  const [atvDialog, setAtvDialog] = useState(false);
  const [atvUsers, setAtvUsers] = useState([]);
  const [atvCanCreate, setAtvCanCreate] = useState(false);
  const [atvForm, setAtvForm] = useState({ titulo: '', descricao: '', atribuido_a_id: '', prioridade: 'media' });

  // Dialog de histórico de etapa (ao clicar em um step da timeline)
  const [stageDetailOpen, setStageDetailOpen] = useState(false);
  const [stageDetail, setStageDetail] = useState(null);
  const [loadingStageDetail, setLoadingStageDetail] = useState(false);

  // Laudos (Fase 3)
  // Laudos (Fase 3) — agora gerenciado dentro do componente LaudosEditor

  // Dialog de resolução de pendência
  const [resolveDialog, setResolveDialog] = useState({ open: false, index: null, texto: '' });

  const openStageDetail = async (etapa) => {
    try {
      setLoadingStageDetail(true);
      setStageDetail({ etapa, entradas: [], atividades: [] });
      setStageDetailOpen(true);
      const token = localStorage.getItem('agrolink_token');
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${project.id}/etapa-historico/${etapa.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStageDetail({ etapa, ...resp.data });
    } catch (error) {
      toast.error('Erro ao carregar histórico da etapa');
    } finally {
      setLoadingStageDetail(false);
    }
  };
  const [loadingEtapas, setLoadingEtapas] = useState(true);
  const [editValorDialog, setEditValorDialog] = useState(false);
  const [novoValor, setNovoValor] = useState('');
  const [canEditValue, setCanEditValue] = useState(false);
  const [savingValor, setSavingValor] = useState(false);
  const [canRegress, setCanRegress] = useState(false);

  // Carregar etapas filtradas por tipo de projeto
  const loadEtapas = useCallback(async () => {
    try {
      setLoadingEtapas(true);
      const tipoProjetoId = project.tipo_projeto_id;
      if (tipoProjetoId) {
        // Usar endpoint que filtra por tipo de projeto
        const response = await etapasAPI.listByTipoProjeto(tipoProjetoId);
        setEtapas(response.data);
      } else {
        // Fallback: usar etapas passadas por props ou listar todas
        if (etapasFromProps && etapasFromProps.length > 0) {
          setEtapas(etapasFromProps);
        } else {
          const response = await etapasAPI.list();
          setEtapas(response.data);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar etapas:', error);
      // Fallback em caso de erro
      if (etapasFromProps && etapasFromProps.length > 0) {
        setEtapas(etapasFromProps);
      }
    } finally {
      setLoadingEtapas(false);
    }
  }, [project.tipo_projeto_id, etapasFromProps]);

  // Carregar requisitos dinâmicos filtrados por tipo de projeto
  const loadRequisitos = useCallback(async () => {
    try {
      setLoadingRequisitos(true);
      // Usar o novo endpoint que filtra por tipo de projeto
      const tipoProjetoId = project.tipo_projeto_id;
      if (tipoProjetoId) {
        const response = await requisitosEtapaAPI.listByTipoProjeto(tipoProjetoId);
        setRequisitos(response.data);
      } else {
        // Fallback para listar todos se não tiver tipo de projeto
        const response = await requisitosEtapaAPI.list();
        setRequisitos(response.data.filter(r => r.ativo));
      }
    } catch (error) {
      console.error('Erro ao carregar requisitos:', error);
      // Fallback em caso de erro
      try {
        const response = await requisitosEtapaAPI.list();
        setRequisitos(response.data.filter(r => r.ativo));
      } catch (e) {
        console.error('Erro no fallback:', e);
      }
    } finally {
      setLoadingRequisitos(false);
    }
  }, [project.tipo_projeto_id]);

  // Carregar Marcas
  const loadMarcas = useCallback(async () => {
    try {
      const tipoProjetoId = project.tipo_projeto_id;
      const resp = tipoProjetoId
        ? await marcasEtapaAPI.listByTipoProjeto(tipoProjetoId)
        : await marcasEtapaAPI.list();
      setMarcas(resp.data || []);
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
      setMarcas([]);
    }
  }, [project.tipo_projeto_id]);

  useEffect(() => {
    loadEtapas();
  }, [loadEtapas]);

  useEffect(() => {
    loadRequisitos();
  }, [loadRequisitos]);

  useEffect(() => {
    loadMarcas();
  }, [loadMarcas]);

  useEffect(() => {
    setMarcasValores(project.marcas_valores || {});
  }, [project.marcas_valores]);

  // Atividades
  const loadAtividades = useCallback(async () => {
    try {
      const r = await atividadesAPI.list({ projeto_id: project.id });
      setAtividades(r.data || []);
    } catch {
      setAtividades([]);
    }
  }, [project.id]);

  useEffect(() => {
    loadAtividades();
    atividadesAPI.canCreate().then((r) => setAtvCanCreate(!!r.data.can_create)).catch(() => {});
    usersAPI.list().then((r) => setAtvUsers(r.data || [])).catch(() => {});
  }, [loadAtividades]);

  const openNovaAtividade = () => {
    setAtvForm({ titulo: '', descricao: '', atribuido_a_id: '', prioridade: 'media' });
    setAtvDialog(true);
  };

  const submitAtividade = async () => {
    if (!atvForm.titulo.trim() || !atvForm.atribuido_a_id) {
      toast.error('Preencha título e atribuído');
      return;
    }
    try {
      await atividadesAPI.create({ ...atvForm, projeto_id: project.id });
      toast.success('Atividade criada');
      setAtvDialog(false);
      loadAtividades();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar');
    }
  };

  // Verificar se o usuário pode editar o valor do projeto
  useEffect(() => {
    const checkEditPermission = async () => {
      try {
        const token = localStorage.getItem('agrolink_token');
        if (!token) {
          setCanEditValue(false);
          setCanRegress(false);
          return;
        }
        const [editRes, regressRes] = await Promise.all([
          axios.get(`${API_URL}/api/permissions/can-edit-project-value`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/permissions/can-regress-stage`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setCanEditValue(editRes.data.can_edit);
        setCanRegress(regressRes.data.can_regress);
      } catch (error) {
        setCanEditValue(false);
        setCanRegress(false);
      }
    };
    checkEditPermission();
  }, []);

  const handleEditValor = () => {
    setNovoValor(project.valor_credito ? formatCurrencyInput(Math.round(project.valor_credito * 100).toString()) : '');
    setEditValorDialog(true);
  };

  const handleSaveValor = async () => {
    const valorNumerico = parseCurrencyInput(novoValor);
    if (isNaN(valorNumerico) || valorNumerico < 0) {
      toast.error('Valor inválido');
      return;
    }

    try {
      setSavingValor(true);
      const token = localStorage.getItem('agrolink_token');
      await axios.put(`${API_URL}/api/projects/${project.id}/valor`, 
        { valor_credito: valorNumerico },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Valor do projeto atualizado!');
      setEditValorDialog(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar valor');
    } finally {
      setSavingValor(false);
    }
  };

  const currentEtapaIndex = etapas.findIndex(e => e.id === project.etapa_atual_id);
  const isLastStage = currentEtapaIndex === etapas.length - 1;
  const currentEtapaNome = project.etapa_atual_nome || '';
  const currentEtapaId = project.etapa_atual_id || '';

  // Filtrar requisitos para a etapa atual (já filtrados por tipo de projeto no backend)
  const currentEtapaRequisitos = requisitos.filter(r => r.etapa_id === currentEtapaId);

  // === MARCAS ===
  // Marcas da etapa atual (editáveis)
  const marcasDaEtapaAtual = marcas.filter((m) => m.etapa_id === currentEtapaId);
  // Marcas de etapas anteriores já preenchidas (read-only, persistem)
  const marcasHerdadas = marcas.filter((m) => {
    if (m.etapa_id === currentEtapaId) return false;
    const origemIndex = etapas.findIndex((e) => e.id === m.etapa_id);
    return origemIndex >= 0 && origemIndex < currentEtapaIndex;
  });

  const handleSaveMarca = async (marca, valor) => {
    setMarcasValores((prev) => ({ ...prev, [marca.campo]: valor }));
    try {
      await marcasEtapaAPI.saveProjectMarca(project.id, marca.id, valor);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar marca');
    }
  };

  const renderMarcaInput = (marca, readOnly = false) => {
    const valor = marcasValores[marca.campo] ?? '';
    const isNumero = marca.tipo_campo === 'numero';
    const inputType = marca.tipo_campo === 'data' ? 'date' : 'text';
    const handleChange = (e) => {
      let v = e.target.value;
      if (isNumero) v = v.replace(/\D/g, ''); // só dígitos, sem ponto/vírgula
      setMarcasValores((prev) => ({ ...prev, [marca.campo]: v }));
    };
    return (
      <div key={marca.id} className="space-y-1">
        <Label htmlFor={`marca-${marca.id}`} className="text-sm">
          {marca.nome}
          {marca.obrigatoria && !readOnly && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <Input
          id={`marca-${marca.id}`}
          type={inputType}
          inputMode={isNumero ? 'numeric' : undefined}
          pattern={isNumero ? '[0-9]*' : undefined}
          value={valor}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={handleChange}
          onBlur={(e) => !readOnly && handleSaveMarca(marca, e.target.value)}
          data-testid={`marca-input-${marca.campo}`}
          className={readOnly ? 'cursor-not-allowed opacity-80' : ''}
          placeholder={readOnly ? '' : `Digite ${marca.nome.toLowerCase()}...`}
        />
      </div>
    );
  };

  const renderMarcasSection = () => {
    if (marcasDaEtapaAtual.length === 0 && marcasHerdadas.length === 0) return null;
    const todasMarcas = [...marcasHerdadas.map((m) => ({ m, readOnly: true })), ...marcasDaEtapaAtual.map((m) => ({ m, readOnly: false }))];
    return (
      <div className="space-y-3" data-testid="marcas-section">
        <h4 className="font-semibold">Dados Complementares</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {todasMarcas.map(({ m, readOnly }) => renderMarcaInput(m, readOnly))}
        </div>
      </div>
    );
  };

  const getEtapaStatus = (etapa, index) => {
    if (index < currentEtapaIndex) return 'completed';
    if (index === currentEtapaIndex) {
      const currentHistorico = project.historico_etapas?.find(h => h.etapa_id === etapa.id);
      const hasPendencia = currentHistorico?.pendencias?.some(p => !p.resolvida);
      return hasPendencia ? 'pending' : 'active';
    }
    return 'upcoming';
  };

  const getEtapaDuration = (etapaId) => {
    const historico = project.historico_etapas?.find(h => h.etapa_id === etapaId);
    if (!historico) return null;

    if (historico.data_fim) {
      return historico.dias_duracao;
    }

    const start = new Date(historico.data_inicio);
    const now = new Date();
    return Math.floor((now - start) / (1000 * 60 * 60 * 24));
  };

  const handleSaveExtraFields = async () => {
    try {
      await projectsAPI.updateDocuments(project.id, {
        numero_contrato: numeroContrato,
        valor_servico: valorServico ? parseCurrencyInput(valorServico) : null,
      });
      toast.success('Dados salvos com sucesso');
      onUpdate();
    } catch (error) {
      toast.error('Erro ao salvar dados');
    }
  };

  // ===== Laudos (Fase 3) — agora delegado ao LaudosEditor =====
  const renderLaudosSection = () => (
    <LaudosEditor
      projectId={project.id}
      initialLaudos={project.laudos || []}
    />
  );

  // ===== Parcelas do Serviço (Fase 4) — agora delegado ao ParcelasEditor =====
  const renderParcelasSection = () => (
    <ParcelasEditor
      projectId={project.id}
      initialParcelas={project.parcelas_servico || []}
      initialParcelado={project.servico_parcelado === true}
    />
  );

  // Render stage-specific checklist using dynamic requisitos
  const renderStageChecklist = () => {
    const docs = project.documentos_check || {};

    // Se está carregando requisitos, mostrar skeleton
    if (loadingRequisitos) {
      return (
        <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      );
    }

    // Se não há requisitos para esta etapa
    if (currentEtapaRequisitos.length === 0) {
      // Verificar se é uma das etapas especiais com campos extras
      if (currentEtapaNome.includes('Instrumento de Crédito')) {
        // Se já existe uma Marca dinâmica para "numero_do_contrato" nesta etapa,
        // não renderiza o campo legado (a Marca cuida do input).
        const hasContratoMarca = (marcas || []).some(m => m.campo === 'numero_do_contrato');
        if (hasContratoMarca) return null;
        return (
          <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
            <h4 className="font-medium">Dados do Contrato</h4>
            <div className="space-y-2">
              <Label htmlFor="numero_contrato">Número do Contrato</Label>
              <div className="flex gap-2">
                <Input
                  id="numero_contrato"
                  placeholder="Digite o número do contrato"
                  value={numeroContrato}
                  onChange={(e) => setNumeroContrato(e.target.value)}
                  data-testid="input-numero-contrato"
                  className="flex-1"
                />
                <Button onClick={handleSaveExtraFields} size="sm" data-testid="save-contrato-btn">
                  Salvar
                </Button>
              </div>
            </div>
          </div>
        );
      }
      
      if (currentEtapaNome.includes('Projeto Creditado')) {
        return (
          <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
            <h4 className="font-medium">Dados do Serviço</h4>
            <div className="space-y-2">
              <Label htmlFor="valor_servico">Valor do Serviço</Label>
              <div className="flex gap-2">
                <Input
                  id="valor_servico"
                  type="text"
                  placeholder="R$ 0,00"
                  value={valorServico}
                  onChange={(e) => setValorServico(formatCurrencyInput(e.target.value))}
                  data-testid="input-valor-servico"
                  className="flex-1"
                />
                <Button onClick={handleSaveExtraFields} size="sm" data-testid="save-servico-btn">
                  Salvar
                </Button>
              </div>
            </div>
            {renderParcelasSection()}
            {renderLaudosSection()}
          </div>
        );
      }
      
      return null;
    }

    // Verificar se há algum requisito pendente
    const hasPending = currentEtapaRequisitos.some(req => !docs[req.campo]);

    return (
      <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
        <div>
          <h4 className="font-medium mb-3">Requisitos da Etapa</h4>
          <div className="flex flex-col gap-3">
            {currentEtapaRequisitos.map((req) => {
              const tipoCampo = req.tipo_campo || 'checkbox';
              
              if (tipoCampo === 'checkbox') {
                return (
                  <div key={req.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={req.campo}
                      checked={docs[req.campo] || false}
                      onCheckedChange={(v) => handleDocumentCheck(req.campo, v)}
                      data-testid={`doc-${req.campo}`}
                    />
                    <Label htmlFor={req.campo} className="text-sm cursor-pointer">
                      {req.nome}
                    </Label>
                  </div>
                );
              }
              
              if (tipoCampo === 'data') {
                return (
                  <div key={req.id} className="space-y-1">
                    <Label htmlFor={req.campo} className="text-sm">{req.nome}</Label>
                    <Input
                      id={req.campo}
                      type="date"
                      value={docs[req.campo] || ''}
                      onChange={(e) => handleDocumentCheck(req.campo, e.target.value)}
                      data-testid={`doc-${req.campo}`}
                      className="max-w-xs"
                    />
                  </div>
                );
              }
              
              if (tipoCampo === 'numero') {
                return (
                  <div key={req.id} className="space-y-1">
                    <Label htmlFor={req.campo} className="text-sm">{req.nome}</Label>
                    <Input
                      id={req.campo}
                      type="number"
                      placeholder="0"
                      value={docs[req.campo] || ''}
                      onChange={(e) => handleDocumentCheck(req.campo, e.target.value)}
                      data-testid={`doc-${req.campo}`}
                      className="max-w-xs"
                    />
                  </div>
                );
              }
              
              if (tipoCampo === 'moeda') {
                return (
                  <div key={req.id} className="space-y-1">
                    <Label htmlFor={req.campo} className="text-sm">{req.nome}</Label>
                    <Input
                      id={req.campo}
                      type="text"
                      placeholder="R$ 0,00"
                      value={docs[req.campo] || ''}
                      onChange={(e) => handleDocumentCheck(req.campo, formatCurrencyInput(e.target.value))}
                      data-testid={`doc-${req.campo}`}
                      className="max-w-xs"
                    />
                  </div>
                );
              }
              
              if (tipoCampo === 'caixa_texto') {
                return (
                  <div key={req.id} className="space-y-1">
                    <Label htmlFor={req.campo} className="text-sm">{req.nome}</Label>
                    <Textarea
                      id={req.campo}
                      placeholder={`Digite ${req.nome.toLowerCase()}...`}
                      value={docs[req.campo] || ''}
                      onChange={(e) => handleDocumentCheck(req.campo, e.target.value)}
                      data-testid={`doc-${req.campo}`}
                      rows={3}
                    />
                  </div>
                );
              }
              
              // tipo_campo === 'texto' (default for non-checkbox)
              return (
                <div key={req.id} className="space-y-1">
                  <Label htmlFor={req.campo} className="text-sm">{req.nome}</Label>
                  <Input
                    id={req.campo}
                    placeholder={`Digite ${req.nome.toLowerCase()}...`}
                    value={docs[req.campo] || ''}
                    onChange={(e) => handleDocumentCheck(req.campo, e.target.value)}
                    data-testid={`doc-${req.campo}`}
                    className="max-w-sm"
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Campos extras para etapas específicas */}
        {/* (Nº da Proposta removido — agora pode ser configurado em /admin/marcas) */}

        {currentEtapaNome.includes('Instrumento de Crédito') && !(marcas || []).some(m => m.campo === 'numero_do_contrato') && (
          <div className="space-y-2 pt-3 border-t">
            <Label htmlFor="numero_contrato">Número do Contrato</Label>
            <div className="flex gap-2">
              <Input
                id="numero_contrato"
                placeholder="Digite o número do contrato"
                value={numeroContrato}
                onChange={(e) => setNumeroContrato(e.target.value)}
                data-testid="input-numero-contrato"
                className="flex-1"
              />
              <Button onClick={handleSaveExtraFields} size="sm" data-testid="save-contrato-btn">
                Salvar
              </Button>
            </div>
          </div>
        )}

        {currentEtapaNome.includes('Projeto Creditado') && (
          <div className="space-y-2 pt-3 border-t">
            <Label htmlFor="valor_servico">Valor do Serviço</Label>
            <div className="flex gap-2">
              <Input
                id="valor_servico"
                type="text"
                placeholder="R$ 0,00"
                value={valorServico}
                onChange={(e) => setValorServico(formatCurrencyInput(e.target.value))}
                data-testid="input-valor-servico"
                className="flex-1"
              />
              <Button onClick={handleSaveExtraFields} size="sm" data-testid="save-servico-btn">
                Salvar
              </Button>
            </div>
            {renderParcelasSection()}
            {renderLaudosSection()}
          </div>
        )}

        {hasPending && (
          <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Documentos/requisitos pendentes — pode impedir avanço para próxima etapa
          </p>
        )}
      </div>
    );
  };

  const handleNextStage = async () => {
    try {
      setLoading(true);
      await projectsAPI.nextStage(project.id);
      toast.success('Projeto avançado para próxima etapa');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao avançar etapa');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousStage = async () => {
    try {
      setLoading(true);
      await projectsAPI.previousStage(project.id);
      toast.success('Projeto retornou para etapa anterior');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao voltar etapa');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    try {
      setLoading(true);
      await projectsAPI.archive(project.id);
      toast.success('Projeto arquivado com sucesso');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao arquivar projeto');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelMotivo.trim()) {
      toast.error('Informe o motivo da desistência');
      return;
    }
    try {
      setLoading(true);
      await projectsAPI.cancel(project.id, { motivo: cancelMotivo });
      toast.success('Projeto cancelado');
      setCancelDialog(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar projeto');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPendencia = async () => {
    if (!newPendencia.trim()) {
      toast.error('Informe a descrição da pendência');
      return;
    }
    try {
      setLoading(true);
      await projectsAPI.addPendencia(project.id, { descricao: newPendencia });
      toast.success('Pendência adicionada');
      setPendenciaDialog(false);
      setNewPendencia('');
      onUpdate();
    } catch (error) {
      toast.error('Erro ao adicionar pendência');
    } finally {
      setLoading(false);
    }
  };

  const handleResolvePendencia = async (index, resolucaoTexto = '') => {
    try {
      await projectsAPI.resolvePendencia(project.id, index, resolucaoTexto);
      toast.success('Pendência resolvida');
      onUpdate();
    } catch (error) {
      toast.error('Erro ao resolver pendência');
    }
  };

  // v1.7 — Suspender / Retomar projeto
  const handleSuspenderProjeto = async () => {
    const motivo = window.prompt('Motivo da suspensão (opcional):');
    if (motivo === null) return; // cancelou
    try {
      setLoading(true);
      await projectsAPI.suspender(project.id, motivo || '');
      toast.success('Projeto suspenso');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao suspender projeto');
    } finally {
      setLoading(false);
    }
  };

  const handleRetomarProjeto = async () => {
    if (!window.confirm('Retomar este projeto? Ele voltará ao fluxo normal.')) return;
    try {
      setLoading(true);
      await projectsAPI.retomar(project.id);
      toast.success('Projeto retomado');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao retomar projeto');
    } finally {
      setLoading(false);
    }
  };

  const handleAddObservacao = async () => {
    if (!newObservacao.trim()) {
      toast.error('Informe a observação');
      return;
    }
    try {
      setLoading(true);
      await projectsAPI.addObservacao(project.id, { texto: newObservacao });
      toast.success('Observação adicionada');
      setObservacaoDialog(false);
      setNewObservacao('');
      onUpdate();
    } catch (error) {
      toast.error('Erro ao adicionar observação');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentCheck = async (field, value) => {
    try {
      await projectsAPI.updateDocuments(project.id, { [field]: value });
      onUpdate();
    } catch (error) {
      toast.error('Erro ao atualizar documento');
    }
  };

  const loadFiles = async () => {
    try {
      const response = await filesAPI.list(project.cliente_id);
      // Backend agora retorna array direto
      const filesData = Array.isArray(response.data) ? response.data : (response.data.files || []);
      setFiles(filesData);
    } catch (error) {
      toast.error('Erro ao carregar arquivos');
    }
  };

  const handleOpenDocumentos = async () => {
    setDocumentosDialog(true);
    await loadFiles();
  };

  const handleOpenProjeto = async () => {
    setProjetoDialog(true);
    await loadProjetoFiles();
  };

  const loadProjetoFiles = async () => {
    try {
      const response = await filesAPI.list(`projeto_${project.id}`);
      setProjetoFiles(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar arquivos do projeto:', error);
      setProjetoFiles([]);
    }
  };

  const handleProjetoFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    // Validar tamanho de todos os arquivos
    const invalidFiles = selectedFiles.filter(f => f.size > limiteUpload.bytes);
    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.length} arquivo(s) excedem o limite de ${limiteUpload.mb} MB`);
      return;
    }

    try {
      setUploadingProjeto(true);
      let successCount = 0;
      let errorCount = 0;

      // Upload de cada arquivo para pasta do projeto
      for (const file of selectedFiles) {
        try {
          await filesAPI.upload(`projeto_${project.id}`, file);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Erro ao enviar ${file.name}:`, error);
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} arquivo(s) do projeto enviado(s)`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} arquivo(s) falharam`);
      }

      await loadProjetoFiles();
      e.target.value = '';
    } catch (error) {
      toast.error('Erro ao enviar arquivos do projeto');
    } finally {
      setUploadingProjeto(false);
    }
  };

  const handleProjetoDownload = async (filename) => {
    try {
      const response = await filesAPI.download(`projeto_${project.id}`, filename);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleProjetoDelete = async (filename) => {
    if (!window.confirm(`Excluir ${filename}?`)) return;
    try {
      await filesAPI.delete(`projeto_${project.id}`, filename);
      toast.success('Arquivo excluído');
      await loadProjetoFiles();
    } catch (error) {
      toast.error('Erro ao excluir arquivo');
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    // Validar tamanho de todos os arquivos
    const invalidFiles = selectedFiles.filter(f => f.size > limiteUpload.bytes);
    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.length} arquivo(s) excedem o limite de ${limiteUpload.mb} MB`);
      return;
    }

    try {
      setUploading(true);
      let successCount = 0;
      let errorCount = 0;

      // Upload de cada arquivo
      for (const file of selectedFiles) {
        try {
          await filesAPI.upload(project.cliente_id, file);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Erro ao enviar ${file.name}:`, error);
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} arquivo(s) enviado(s) com sucesso`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} arquivo(s) falharam`);
      }

      await loadFiles();
      // Limpar input
      e.target.value = '';
    } catch (error) {
      toast.error('Erro ao enviar arquivos');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const response = await filesAPI.download(project.cliente_id, filename);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDeleteFile = async (filename) => {
    try {
      await filesAPI.delete(project.cliente_id, filename);
      toast.success('Arquivo excluído');
      await loadFiles();
    } catch (error) {
      toast.error('Erro ao excluir arquivo');
    }
  };

  const currentHistorico = project.historico_etapas?.[project.historico_etapas.length - 1];
  const pendencias = currentHistorico?.pendencias || [];
  const observacoes = currentHistorico?.observacoes || [];

  return (
    <div className="p-6 space-y-6">
      {/* Motivo da Desistência */}
      {project.status === 'desistido' && project.motivo_desistencia && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800" data-testid="motivo-desistencia">
          <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-800 dark:text-red-400">Projeto Desistido</p>
            <p className="text-sm text-red-700 dark:text-red-500 mt-1">{project.motivo_desistencia}</p>
          </div>
        </div>
      )}

      {/* Timeline — stepper redesenhado (clean) */}
      <div className="relative">
        {loadingEtapas ? (
          <div className="flex items-center justify-between overflow-x-auto pb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center flex-shrink-0">
                <div className="flex flex-col items-center">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <Skeleton className="mt-2 h-4 w-16" />
                </div>
                {i < 5 && <Skeleton className="w-12 h-0.5 mx-2" />}
              </div>
            ))}
          </div>
        ) : (() => {
          // Etapas visíveis (prioridade: atual, anterior imediata, e todas futuras)
          const visibleIndexes = new Set();
          etapas.forEach((_, idx) => {
            if (idx === currentEtapaIndex) visibleIndexes.add(idx);
            if (idx === currentEtapaIndex - 1) visibleIndexes.add(idx);
            if (idx > currentEtapaIndex) visibleIndexes.add(idx);
          });
          // Etapas ocultas (antigas, antes da anterior imediata)
          const hiddenEtapas = etapas
            .map((e, idx) => ({ ...e, idx }))
            .filter((x) => x.idx < currentEtapaIndex - 1);

          return (
            <div className="space-y-3">
              {/* Stepper principal */}
              <div className="flex items-start overflow-x-auto pb-2 gap-0">
                {/* Botão "ver histórico" (aparece quando há etapas ocultas) */}
                {hiddenEtapas.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setHistoryOpen((v) => !v)}
                    data-testid="timeline-history-toggle"
                    className={cn(
                      'flex flex-col items-center flex-shrink-0 mr-2 transition-all group',
                      'hover:opacity-100 opacity-70'
                    )}
                    title={`Ver ${hiddenEtapas.length} etapa(s) anterior(es)`}
                  >
                    <div className="h-12 flex items-center">
                      <div className="w-10 h-10 rounded-full border-2 border-dashed border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                        <History className="w-4 h-4 text-emerald-600" />
                      </div>
                    </div>
                    <span className="mt-2 text-[11px] font-medium text-muted-foreground">
                      {hiddenEtapas.length} concluída{hiddenEtapas.length > 1 ? 's' : ''}
                    </span>
                    <span className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
                      {historyOpen ? 'Ocultar' : 'Expandir'}
                      <ChevronDown className={cn('w-3 h-3 transition-transform', historyOpen && 'rotate-180')} />
                    </span>
                  </button>
                )}

                {etapas.map((etapa, index) => {
                  const status = getEtapaStatus(etapa, index);
                  const duration = getEtapaDuration(etapa.id);
                  const shouldShow = visibleIndexes.has(index) || historyOpen;
                  if (!shouldShow) return null;

                  const isActive = index === currentEtapaIndex;
                  return (
                    <div key={etapa.id} className="flex items-start" style={{ flex: '1 1 0', minWidth: isActive ? 120 : 80 }}>
                      <div className="flex flex-col items-center w-full">
                        {/* Container com altura fixa para alinhar todos os círculos no mesmo eixo */}
                        <div className="flex items-center w-full h-12">
                          {index > 0 && (visibleIndexes.has(index - 1) || historyOpen) ? (
                            <div className={cn(
                              'h-[2px] flex-1 rounded-full',
                              index <= currentEtapaIndex ? 'bg-emerald-500' : 'bg-muted'
                            )} />
                          ) : (
                            <div className="flex-1" />
                          )}
                          <div
                            className={cn(
                              // Tamanho fixo para alinhamento — destaque por ring + scale
                              'w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 cursor-pointer',
                              'hover:ring-2 hover:ring-primary/40',
                              isActive && 'scale-110 shadow-lg shadow-primary/30 ring-4 ring-primary/20',
                              status === 'completed' && 'bg-emerald-500 border-emerald-500 text-white',
                              status === 'active' && 'bg-primary border-primary text-primary-foreground',
                              status === 'pending' && 'bg-amber-500 border-amber-500 text-white',
                              status === 'upcoming' && 'bg-background border-muted-foreground/30 text-muted-foreground'
                            )}
                            onClick={() => openStageDetail(etapa)}
                            title={`Ver histórico: ${etapa.nome}`}
                            data-testid={`timeline-step-${index}`}
                          >
                            {status === 'completed' ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : status === 'pending' ? (
                              <AlertTriangle className="w-5 h-5" />
                            ) : status === 'active' ? (
                              <Circle className="w-5 h-5 fill-current" />
                            ) : (
                              <span className="text-sm font-semibold">{index + 1}</span>
                            )}
                          </div>
                          {index < etapas.length - 1 && (visibleIndexes.has(index + 1) || historyOpen) ? (
                            <div className={cn(
                              'h-[2px] flex-1 rounded-full',
                              index < currentEtapaIndex ? 'bg-emerald-500' : 'bg-muted'
                            )} />
                          ) : (
                            <div className="flex-1" />
                          )}
                        </div>
                        <span className={cn(
                          'mt-2 text-center w-full px-1 leading-tight',
                          isActive ? 'text-[13px] font-semibold text-foreground' : 'text-[11px] font-medium',
                          status === 'upcoming' ? 'text-muted-foreground' : 'text-foreground'
                        )}>
                          {etapa.nome}
                        </span>
                        {duration !== null && (
                          <Badge variant="outline" className="mt-1 text-[10px] h-4 px-1.5 font-normal">
                            <Clock className="w-2.5 h-2.5 mr-0.5" />
                            {duration}d
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Progresso */}
              <div className="flex items-center gap-2 px-1">
                <span className="text-xs text-muted-foreground tabular-nums">
                  Etapa {currentEtapaIndex + 1} de {etapas.length}
                </span>
                <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-primary transition-all duration-500"
                    style={{ width: `${((currentEtapaIndex + 1) / etapas.length) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground tabular-nums font-medium">
                  {Math.round(((currentEtapaIndex + 1) / etapas.length) * 100)}%
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Marcas — Dados Complementares */}
      {renderMarcasSection()}

      {/* Requisitos da Etapa */}
      {renderStageChecklist()}

      {/* Atividades */}
      <div className="space-y-1.5" data-testid="atividades-section">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">Atividades</h4>
          {atvCanCreate && (
            <Button size="sm" variant="outline" onClick={openNovaAtividade} data-testid="nova-atividade-btn" className="h-7 text-xs">
              <Plus className="w-3 h-3 mr-1" />
              Nova
            </Button>
          )}
        </div>
        {atividades.filter((a) => a.status === 'pendente').length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Nenhuma atividade pendente.</p>
        ) : (
          <div className="space-y-1">
            {atividades.filter((a) => a.status === 'pendente').map((a) => {
              const statusColor = a.status === 'pendente' ? 'bg-amber-500' : a.status === 'resolvida' ? 'bg-emerald-500' : 'bg-slate-400';
              const prioColor = a.prioridade === 'alta' ? 'text-red-600' : a.prioridade === 'media' ? 'text-amber-600' : 'text-slate-500';
              return (
                <div
                  key={a.id}
                  onClick={() => navigate(`/atividades?id=${a.id}`)}
                  className="flex items-center gap-2 p-1.5 rounded border bg-card hover:bg-muted/40 transition-colors cursor-pointer text-sm"
                  data-testid={`atividade-row-${a.id}`}
                >
                  <span className={cn('w-2 h-2 rounded-full flex-shrink-0', statusColor)} />
                  <span className="flex-1 truncate">{a.titulo}</span>
                  <span className={cn('text-xs font-medium', prioColor)}>
                    {a.prioridade === 'alta' ? 'Alta' : a.prioridade === 'media' ? 'Média' : 'Baixa'}
                  </span>
                  <span className="text-xs text-muted-foreground hidden sm:inline">→ {a.atribuido_a_nome}</span>
                  {a.respostas?.length > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                      <MessageSquare className="w-3 h-3" />
                      {a.respostas.length}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pendências */}
      {pendencias.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="font-semibold text-sm">Pendências</h4>
          <div className="space-y-1">
            {pendencias.map((p, idx) => (
              <div
                key={idx}
                className={cn(
                  'p-2 rounded-lg border text-sm',
                  p.resolvida ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/10 border-red-500/30'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {p.resolvida ? (
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                    )}
                    <span className={cn('truncate', p.resolvida && 'line-through text-muted-foreground')}>
                      {p.descricao}
                    </span>
                  </div>
                  {!p.resolvida && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setResolveDialog({ open: true, index: idx, texto: '' })}
                      data-testid={`resolve-pendencia-${idx}`}
                      className="h-7 text-xs flex-shrink-0"
                    >
                      Resolver
                    </Button>
                  )}
                </div>
                {p.criado_por_nome && (
                  <p className="text-[11px] text-muted-foreground ml-5 mt-0.5">
                    por {p.criado_por_nome}
                    {p.data_criacao && ` · ${new Date(p.data_criacao).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`}
                  </p>
                )}
                {p.resolvida && (
                  <div className="ml-5 mt-1.5 p-1.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-xs">
                    <p className="font-medium text-emerald-700 dark:text-emerald-400">Resolução:</p>
                    {p.resolucao_texto ? (
                      <p className="whitespace-pre-wrap text-foreground">{p.resolucao_texto}</p>
                    ) : (
                      <p className="italic text-muted-foreground">Sem detalhes</p>
                    )}
                    {(p.resolvida_por_nome || p.data_resolucao) && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {p.resolvida_por_nome && <>por {p.resolvida_por_nome}</>}
                        {p.data_resolucao && ` · ${new Date(p.data_resolucao).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Observações */}
      {observacoes.length > 0 && (
        <div className="space-y-1.5">
          <h4 className="font-semibold text-sm">Observações</h4>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {observacoes.map((obs, idx) => (
              <div key={idx} className="p-2 rounded-lg bg-muted/30 border text-sm">
                <p className="whitespace-pre-wrap">{obs.texto}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {obs.usuario_nome} · {new Date(obs.data).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions — agrupadas em 2 linhas: secundárias (esq) e progresso (dir) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-3 border-t">
        {/* Grupo Esquerdo — ações secundárias */}
        <div className="flex flex-wrap gap-1.5 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPendenciaDialog(true)}
            data-testid="add-pendencia-btn"
            className="h-8"
          >
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
            Pendência
          </Button>
          {project.status === 'suspenso' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetomarProjeto}
              data-testid="retomar-projeto-btn"
              className="h-8 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
            >
              <PlayCircle className="w-3.5 h-3.5 mr-1.5" />
              Retomar Projeto
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSuspenderProjeto}
              data-testid="suspender-projeto-btn"
              className="h-8 border-amber-500 text-amber-700 hover:bg-amber-50"
            >
              <PauseCircle className="w-3.5 h-3.5 mr-1.5" />
              Suspender Projeto
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setObservacaoDialog(true)}
            data-testid="add-observacao-btn"
            className="h-8"
          >
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
            Observação
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenDocumentos}
            data-testid="view-documents-btn"
            className="h-8"
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" />
            Documentos
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenProjeto}
            data-testid="view-projeto-btn"
            className="h-8"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Projeto
          </Button>
          {canEditValue && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleEditValor}
              data-testid="edit-valor-btn"
              className="h-8 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
            >
              <DollarSign className="w-3.5 h-3.5 mr-1.5" />
              Editar Valor
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowClientQuickView(true)}
            data-testid="view-client-btn"
            className="h-8"
          >
            <Eye className="w-3.5 h-3.5 mr-1.5" />
            Ver Cliente
          </Button>
          {project.cliente_telefone && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const phone = project.cliente_telefone.replace(/\D/g, '');
                const formattedPhone = phone.startsWith('55') ? phone : `55${phone}`;
                window.open(`https://wa.me/${formattedPhone}`, '_blank');
              }}
              data-testid="whatsapp-btn"
              className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
              title="WhatsApp"
            >
              <MessageCircle className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>

        {/* Grupo Direito — Progresso da etapa (Voltar / Desistir / Próxima ou Arquivar) */}
        <div className="flex flex-wrap gap-1.5 items-center sm:justify-end">
          {canRegress && currentEtapaIndex > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousStage}
              disabled={loading}
              data-testid="previous-stage-btn"
              className="h-8"
            >
              <ChevronLeft className="w-3.5 h-3.5 mr-1.5" />
              Voltar
            </Button>
          )}
          <AlertDialog open={cancelDialog} onOpenChange={setCancelDialog}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" data-testid="cancel-project-btn" className="h-8">
                <XCircle className="w-3.5 h-3.5 mr-1.5" />
                Desistir
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Desistir do Projeto</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação irá cancelar o projeto e excluir todos os documentos. Informe o motivo:
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Textarea
                placeholder="Motivo da desistência..."
                value={cancelMotivo}
                onChange={(e) => setCancelMotivo(e.target.value)}
                data-testid="cancel-motivo"
              />
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleCancel} disabled={loading}>
                  Confirmar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {isLastStage ? (
            <Button onClick={handleArchive} size="sm" disabled={loading} data-testid="archive-project-btn" className="h-8">
              <Archive className="w-3.5 h-3.5 mr-1.5" />
              Arquivar
            </Button>
          ) : (
            <Button onClick={handleNextStage} size="sm" disabled={loading} data-testid="next-stage-btn" className="h-8">
              Próxima Etapa
              <ChevronRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Resolver Pendência Dialog */}
      <Dialog open={resolveDialog.open} onOpenChange={(o) => setResolveDialog((s) => ({ ...s, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolver Pendência</DialogTitle>
            <DialogDescription>
              Descreva como a pendência foi resolvida (opcional). Esse texto fica registrado no histórico.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Ex: Documento atualizado pelo cliente em 21/02..."
            value={resolveDialog.texto}
            onChange={(e) => setResolveDialog((s) => ({ ...s, texto: e.target.value }))}
            rows={4}
            data-testid="resolucao-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog({ open: false, index: null, texto: '' })}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                await handleResolvePendencia(resolveDialog.index, resolveDialog.texto);
                setResolveDialog({ open: false, index: null, texto: '' });
              }}
              data-testid="confirm-resolucao-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmar Resolução
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pendência Dialog */}
      <Dialog open={pendenciaDialog} onOpenChange={setPendenciaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Pendência</DialogTitle>
            <DialogDescription>
              Informe a descrição da pendência para esta etapa.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Descreva a pendência..."
            value={newPendencia}
            onChange={(e) => setNewPendencia(e.target.value)}
            data-testid="pendencia-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendenciaDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddPendencia} disabled={loading}>
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Observação Dialog */}
      <Dialog open={observacaoDialog} onOpenChange={setObservacaoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Observação</DialogTitle>
            <DialogDescription>
              Adicione uma observação sobre o andamento do projeto.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Digite sua observação..."
            value={newObservacao}
            onChange={(e) => setNewObservacao(e.target.value)}
            data-testid="observacao-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setObservacaoDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddObservacao} disabled={loading}>
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documentos Dialog */}
      <Dialog open={documentosDialog} onOpenChange={setDocumentosDialog}>
        <DialogContent className="w-full sm:max-w-xl md:max-w-3xl lg:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Documentos do Cliente</DialogTitle>
            <DialogDescription>
              Gerencie os documentos do cliente. Limite: {limiteUpload.mb} MB por arquivo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Upload */}
            <div className="space-y-2">
              <Label>Selecione os arquivos (máx. {limiteUpload.mb} MB cada)</Label>
              <Input
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={uploading}
                className="flex-1"
                data-testid="file-upload-input"
              />
              {uploading && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Enviando arquivos...
                </p>
              )}
            </div>

            {/* File list */}
            <ScrollArea className="h-64 border rounded-lg">
              {files.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <FileText className="w-8 h-8 mb-2" />
                  <p>Nenhum arquivo enviado</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {files.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <FileText className="w-5 h-5 flex-shrink-0 text-primary" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB • {file.uploaded_at ? new Date(file.uploaded_at).toLocaleString('pt-BR') : '-'} • {file.uploaded_by_name || 'Sistema'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDownload(file.name)}
                          data-testid={`download-file-${idx}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => handleDeleteFile(file.name)}
                          data-testid={`delete-file-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Projeto Dialog */}
      <Dialog open={projetoDialog} onOpenChange={setProjetoDialog}>
        <DialogContent className="w-full sm:max-w-xl md:max-w-3xl lg:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Arquivos do Projeto</DialogTitle>
            <DialogDescription>
              Gerencie os arquivos específicos deste projeto. Limite: {limiteUpload.mb} MB por arquivo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Upload */}
            <div className="space-y-2">
              <Label>Selecione os arquivos do projeto (máx. {limiteUpload.mb} MB cada)</Label>
              <Input
                type="file"
                multiple
                onChange={handleProjetoFileUpload}
                disabled={uploadingProjeto}
                className="flex-1"
                data-testid="projeto-file-upload-input"
              />
              {uploadingProjeto && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Enviando arquivos do projeto...
                </p>
              )}
            </div>

            {/* File list */}
            <ScrollArea className="h-64 border rounded-lg">
              {projetoFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <Upload className="w-8 h-8 mb-2" />
                  <p>Nenhum arquivo de projeto enviado</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {projetoFiles.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Upload className="w-5 h-5 flex-shrink-0 text-primary" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB • {file.uploaded_at ? new Date(file.uploaded_at).toLocaleString('pt-BR') : '-'} • {file.uploaded_by_name || 'Sistema'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleProjetoDownload(file.name)}
                          data-testid={`download-projeto-file-${idx}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => handleProjetoDelete(file.name)}
                          data-testid={`delete-projeto-file-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Valor */}
      <Dialog open={editValorDialog} onOpenChange={setEditValorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-yellow-600" />
              Editar Valor do Projeto
            </DialogTitle>
            <DialogDescription>
              Altere o valor do crédito do projeto em andamento
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="novo_valor">Novo Valor</Label>
              <Input
                id="novo_valor"
                type="text"
                value={novoValor}
                onChange={(e) => {
                  setNovoValor(formatCurrencyInput(e.target.value));
                }}
                placeholder="R$ 0,00"
                data-testid="edit-valor-input"
              />
              <p className="text-xs text-muted-foreground">
                Valor atual: R$ {project.valor_credito?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditValorDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveValor} disabled={savingValor} data-testid="save-valor-btn">
              {savingValor ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: histórico de uma etapa */}
      <Dialog open={stageDetailOpen} onOpenChange={setStageDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Histórico: {stageDetail?.etapa?.nome}
            </DialogTitle>
            <DialogDescription>
              Todas as pendências, observações e atividades desta etapa.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 -mx-6 px-6">
            {loadingStageDetail ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Carregando...</div>
            ) : (
              <div className="space-y-4 pb-4">
                {(stageDetail?.entradas || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">Esta etapa ainda não foi visitada.</p>
                ) : (
                  (stageDetail?.entradas || []).map((entrada, idx) => (
                    <div key={idx} className="rounded-lg border p-3 space-y-3 bg-card">
                      <div className="flex items-center justify-between gap-2 pb-2 border-b">
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">
                            Passagem #{idx + 1}
                          </span>
                          {entrada.retorno && <Badge variant="outline" className="ml-2 text-[10px] border-orange-400 text-orange-600">Retorno</Badge>}
                          {entrada.reaberta_em && <Badge variant="outline" className="ml-2 text-[10px] border-blue-400 text-blue-600">Reaberta</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {entrada.data_inicio && new Date(entrada.data_inicio).toLocaleDateString('pt-BR')}
                          {entrada.data_fim && ' → ' + new Date(entrada.data_fim).toLocaleDateString('pt-BR')}
                          {!entrada.data_fim && <span className="text-emerald-600 ml-1">(atual)</span>}
                        </div>
                      </div>

                      {/* Pendências */}
                      {(entrada.pendencias || []).length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">Pendências</p>
                          <div className="space-y-1.5">
                            {entrada.pendencias.map((p, i) => (
                              <div key={i} className={cn(
                                'flex items-start gap-2 p-2 rounded border text-sm',
                                p.resolvida ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-900/40' : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/40'
                              )}>
                                {p.resolvida ? <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />}
                                <div className="flex-1 min-w-0">
                                  <p className={p.resolvida ? 'line-through opacity-70' : ''}>{p.descricao || p.texto}</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {p.criado_por_nome && <>por {p.criado_por_nome} · </>}
                                    {p.data && new Date(p.data).toLocaleString('pt-BR')}
                                  </p>
                                  {p.resolvida && (p.resolucao_texto || p.resolvida_por_nome || p.data_resolucao) && (
                                    <div className="mt-2 pt-2 border-t border-emerald-200 dark:border-emerald-900/40">
                                      <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-400 mb-0.5">
                                        Resolução
                                      </p>
                                      {p.resolucao_texto ? (
                                        <p className="whitespace-pre-wrap text-foreground">{p.resolucao_texto}</p>
                                      ) : (
                                        <p className="italic text-muted-foreground">Sem detalhes informados</p>
                                      )}
                                      {(p.resolvida_por_nome || p.data_resolucao) && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                          {p.resolvida_por_nome && <>por {p.resolvida_por_nome}</>}
                                          {p.data_resolucao && ` · ${new Date(p.data_resolucao).toLocaleString('pt-BR')}`}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Observações */}
                      {(entrada.observacoes || []).length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">Observações</p>
                          <div className="space-y-1.5">
                            {entrada.observacoes.map((o, i) => (
                              <div key={i} className="p-2 rounded border bg-muted/40 text-sm">
                                <p className="whitespace-pre-wrap">{o.texto || o.descricao}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {o.usuario_nome && <>{o.usuario_nome} · </>}
                                  {o.data && new Date(o.data).toLocaleString('pt-BR')}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {(entrada.pendencias || []).length === 0 && (entrada.observacoes || []).length === 0 && (
                        <p className="text-xs text-muted-foreground italic">Sem pendências ou observações.</p>
                      )}
                    </div>
                  ))
                )}

                {/* Atividades da etapa */}
                {(stageDetail?.atividades || []).length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Atividades criadas nesta etapa</h5>
                    <div className="space-y-1.5">
                      {stageDetail.atividades.map((a) => (
                        <div key={a.id} className="flex items-center gap-2 p-2 rounded border text-sm">
                          <span className={cn('w-2 h-2 rounded-full flex-shrink-0',
                            a.status === 'pendente' ? 'bg-amber-500' : a.status === 'resolvida' ? 'bg-emerald-500' : 'bg-slate-400')} />
                          <span className="flex-1 truncate">{a.titulo}</span>
                          <Badge variant="outline" className="text-[10px]">{a.status}</Badge>
                          <span className="text-xs text-muted-foreground hidden sm:inline">→ {a.atribuido_a_nome}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStageDetailOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nova Atividade */}
      <Dialog open={atvDialog} onOpenChange={setAtvDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova Atividade</DialogTitle>
            <DialogDescription>Atribuir tarefa vinculada a este projeto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Título *</Label>
              <Input
                value={atvForm.titulo}
                onChange={(e) => setAtvForm({ ...atvForm, titulo: e.target.value })}
                placeholder="Ex: Verificar pendência no CAR"
                data-testid="atv-inline-titulo"
              />
            </div>
            <div className="space-y-1">
              <Label>Descrição</Label>
              <Textarea
                value={atvForm.descricao}
                onChange={(e) => setAtvForm({ ...atvForm, descricao: e.target.value })}
                rows={3}
                data-testid="atv-inline-descricao"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Atribuído a *</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border bg-background text-sm"
                  value={atvForm.atribuido_a_id}
                  onChange={(e) => setAtvForm({ ...atvForm, atribuido_a_id: e.target.value })}
                  data-testid="atv-inline-user"
                >
                  <option value="">Selecione</option>
                  {filtrarUsuariosAtribuicao(atvUsers).map((u) => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Prioridade</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border bg-background text-sm"
                  value={atvForm.prioridade}
                  onChange={(e) => setAtvForm({ ...atvForm, prioridade: e.target.value })}
                  data-testid="atv-inline-prio"
                >
                  <option value="baixa">Baixa</option>
                  <option value="media">Média</option>
                  <option value="alta">Alta</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAtvDialog(false)}>Cancelar</Button>
            <Button onClick={submitAtividade} data-testid="atv-inline-save">
              <Plus className="w-4 h-4 mr-2" /> Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick view do cliente (item 8) - mantém contexto do projeto */}
      {showClientQuickView && (
        <ClientQuickViewModal
          clientId={project.cliente_id}
          onClose={() => setShowClientQuickView(false)}
        />
      )}
    </div>
  );
};

export default ProjectTimeline;
